// Validation types and functions for scenario validation
// Based on Tech Plan validation requirements

import type { Scenario, Scene, PhoneChatReplyOption, InventoryPersonStatus } from './index';
import { isValidPedaCategory } from '../systems/peda-categories';

/**
 * Result of a validation operation
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Detailed validation error with location information
 */
export interface ValidationError {
  message: string;
  path?: string;
  lineNumber?: number;
  field?: string;
}

/**
 * Regex patterns for condition validation
 * As specified in Tech Plan section 5
 */
export const CONDITION_PATTERNS = {
  FLAG_CHECK: /^!?flag:[a-zA-Z_][a-zA-Z0-9_]*$/,
  VARIABLE_COMPARISON: /^var:[a-zA-Z_][a-zA-Z0-9_]*(>|<|>=|<=|==|!=)\d+$/,
  VISITED_CHECK: /^!?visited:[a-zA-Z_][a-zA-Z0-9_]*$/,
} as const;

/**
 * Validates a single atomic condition string syntax.
 */
function validateSingleConditionSyntax(trimmed: string): boolean {
  return (
    CONDITION_PATTERNS.FLAG_CHECK.test(trimmed) ||
    CONDITION_PATTERNS.VARIABLE_COMPARISON.test(trimmed) ||
    CONDITION_PATTERNS.VISITED_CHECK.test(trimmed)
  );
}

/**
 * Validates a condition string syntax.
 * Supports comma-separated AND conditions (e.g. "flag:a,flag:b").
 *
 * @param condition - The condition string to validate
 * @returns true if valid, false otherwise
 */
export function validateConditionSyntax(condition: string): boolean {
  if (!condition || typeof condition !== 'string') {
    return false;
  }

  const trimmed = condition.trim();
  if (trimmed.length === 0) return false;

  if (trimmed.includes(',')) {
    return trimmed
      .split(',')
      .every((part) => validateSingleConditionSyntax(part.trim()));
  }

  return validateSingleConditionSyntax(trimmed);
}

/**
 * Iterates every scene block (recursing into choice option branches).
 * The callback receives the scene and a human-readable path for error reporting.
 */
function forEachSceneBlock(
  scenes: Scene[],
  callback: (scene: Scene, path: string) => void,
  parentPath = 'scenes'
): void {
  if (!Array.isArray(scenes)) return;
  for (let i = 0; i < scenes.length; i++) {
    const scene = scenes[i];
    if (!scene) continue;
    const scenePath = `${parentPath}[${i}](id:"${scene.id}")`;
    callback(scene, scenePath);
    if (scene.type === 'choice') {
      for (let j = 0; j < scene.options.length; j++) {
        const nested = scene.options[j]?.scenes;
        if (nested?.length) {
          forEachSceneBlock(nested, callback, `${scenePath}.options[${j}].scenes`);
        }
      }
    }
  }
}

/**
 * Collects all scene IDs from the flat scene list (recursing into branches)
 */
function collectAllSceneIds(scenes: Scene[]): string[] {
  const collected: string[] = [];
  forEachSceneBlock(scenes, (scene) => collected.push(scene.id));
  return collected;
}

/**
 * Validates that all scene IDs are globally unique
 * As required by Tech Plan: "All scene IDs must be globally unique across the entire scenario"
 * 
 * @param scenario - The scenario to validate
 * @returns ValidationResult with any duplicate scene IDs found
 */
export function validateSceneIdUniqueness(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];
  const allSceneIds = collectAllSceneIds(scenario.scenes);
  const seenIds = new Map<string, number>();

  // Count occurrences of each ID
  for (const id of allSceneIds) {
    seenIds.set(id, (seenIds.get(id) || 0) + 1);
  }

  // Find duplicates
  for (const [id, count] of seenIds.entries()) {
    if (count > 1) {
      errors.push({
        message: `Duplicate scene ID "${id}" found ${count} times. Scene IDs must be globally unique.`,
        field: 'scene.id',
        path: `scenes[multiple locations]`,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/** Collects goto targets from (possibly nested) phone reply options. */
function collectPhoneReplyReferences(
  options: PhoneChatReplyOption[] | undefined,
  path: string,
  references: Array<[string, string]>
): void {
  if (!options?.length) return;
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    if (!option) continue;
    if (option.goto) {
      references.push([option.goto, `${path}[${i}].goto`]);
    }
    collectPhoneReplyReferences(option.replyOptions, `${path}[${i}].replyOptions`, references);
  }
}

/**
 * Collects all scene references (goto) from the flat scene list
 *
 * @param scenes - Array of scenes to traverse
 * @returns Array of [sceneId, path] tuples
 */
function collectAllSceneReferences(scenes: Scene[]): Array<[string, string]> {
  const references: Array<[string, string]> = [];

  forEachSceneBlock(scenes, (scene, scenePath) => {
    if (scene.goto) {
      references.push([scene.goto, `${scenePath}.goto`]);
    }

    if (scene.type === 'choice') {
      for (let j = 0; j < scene.options.length; j++) {
        const option = scene.options[j];
        if (option?.goto) {
          references.push([option.goto, `${scenePath}.options[${j}].goto`]);
        }
      }
    }

    if (scene.type === 'textOverlay' && scene.options) {
      for (let j = 0; j < scene.options.length; j++) {
        const option = scene.options[j];
        if (option?.goto) {
          references.push([option.goto, `${scenePath}.options[${j}].goto`]);
        }
      }
    }

    if (scene.type === 'phoneChat' || scene.type === 'phoneCall') {
      collectPhoneReplyReferences(scene.replyOptions, `${scenePath}.replyOptions`, references);
    }

    if (scene.type === 'clickableScreen') {
      for (let j = 0; j < scene.zones.length; j++) {
        const zone = scene.zones[j];
        if (zone?.goto) {
          references.push([zone.goto, `${scenePath}.zones[${j}].goto`]);
        }
      }
    }
  });

  return references;
}

/**
 * Special scene IDs that are handled by the game engine
 * These don't need to exist in the scenes list
 */
const SPECIAL_SCENE_IDS = new Set([
  '__MAIN_MENU__',
  '__MENU__',
]);

/**
 * Validates that all scene references (gotoScene) point to existing scenes
 * As required by Tech Plan: "Validates all scene references (gotoScene) exist"
 * 
 * @param scenario - The scenario to validate
 * @returns ValidationResult with any invalid scene references found
 */
export function validateSceneReferences(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];
  const allSceneIds = new Set(collectAllSceneIds(scenario.scenes));
  const allReferences = collectAllSceneReferences(scenario.scenes);

  for (const [targetId, path] of allReferences) {
    // Skip special scene IDs
    if (SPECIAL_SCENE_IDS.has(targetId)) {
      continue;
    }
    
    if (!allSceneIds.has(targetId)) {
      errors.push({
        message: `Scene reference to non-existent scene ID "${targetId}" found.`,
        field: 'gotoScene',
        path,
      });
    }
  }

  // Also validate startScene
  if (scenario.metadata?.startScene && !allSceneIds.has(scenario.metadata.startScene)) {
    errors.push({
      message: `Start scene ID "${scenario.metadata.startScene}" does not exist in the scenes list.`,
      field: 'metadata.startScene',
      path: 'metadata.startScene',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates that all skill IDs are unique within the scenario
 * 
 * @param scenario - The scenario to validate
 * @returns ValidationResult with any duplicate skill IDs found
 */
export function validateSkillIdUniqueness(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];
  
  if (!scenario.metadata?.skills || scenario.metadata.skills.length === 0) {
    return { isValid: true, errors };
  }

  const seenIds = new Map<string, number>();

  // Count occurrences of each skill ID
  for (const skill of scenario.metadata.skills) {
    seenIds.set(skill.id, (seenIds.get(skill.id) || 0) + 1);
  }

  // Find duplicates
  for (const [id, count] of seenIds.entries()) {
    if (count > 1) {
      errors.push({
        message: `Duplicate skill ID "${id}" found ${count} times. Skill IDs must be unique.`,
        field: 'skill.id',
        path: `metadata.skills[multiple locations]`,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates that all skills have required fields
 * 
 * @param scenario - The scenario to validate
 * @returns ValidationResult with any missing required fields
 */
export function validateSkillStructure(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];
  
  if (!scenario.metadata?.skills) {
    return { isValid: true, errors };
  }

  for (let i = 0; i < scenario.metadata.skills.length; i++) {
    const skill = scenario.metadata.skills[i];
    if (!skill) continue;
    const path = `metadata.skills[${i}]`;

    if (!skill.id || typeof skill.id !== 'string') {
      errors.push({
        message: `Skill at index ${i} is missing required field "id"`,
        field: 'skill.id',
        path: `${path}.id`,
      });
    }

    if (!skill.name || typeof skill.name !== 'string') {
      errors.push({
        message: `Skill "${skill.id || 'unknown'}" is missing required field "name"`,
        field: 'skill.name',
        path: `${path}.name`,
      });
    }

    if (!skill.description || typeof skill.description !== 'string') {
      errors.push({
        message: `Skill "${skill.id || 'unknown'}" is missing required field "description"`,
        field: 'skill.description',
        path: `${path}.description`,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates that all skill and achievement references in text overlay actions exist
 * 
 * @param scenario - The scenario to validate
 * @returns ValidationResult with any invalid references
 */
export function validateTextOverlayActionReferences(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];
  const skillIds = new Set(scenario.metadata?.skills?.map(s => s.id) || []);
  const achievementIds = new Set(scenario.metadata?.achievements?.map(a => a.id) || []);

  forEachSceneBlock(scenario.scenes, (scene, scenePath) => {
    if (scene.type !== 'textOverlay' || !scene.options) return;
    const label = 'Text overlay';
    for (let l = 0; l < scene.options.length; l++) {
      const option = scene.options[l];
      if (!option?.actions) continue;
      for (let m = 0; m < option.actions.length; m++) {
        const action = option.actions[m];
        if (!action) continue;
        if (action.command === 'unlockSkill' && !skillIds.has(action.target)) {
          errors.push({
            message: `${label} option references non-existent skill ID "${action.target}"`,
            field: 'action.target',
            path: `${scenePath}.options[${l}].actions[${m}].target`,
          });
        }
        if (action.command === 'unlockAchievement' && !achievementIds.has(action.target)) {
          errors.push({
            message: `${label} option references non-existent achievement ID "${action.target}"`,
            field: 'action.target',
            path: `${scenePath}.options[${l}].actions[${m}].target`,
          });
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Collects all conditions from a scenario (from choice options and click actions)
 * 
 * @param scenario - The scenario to scan
 * @returns Array of [condition, path] tuples
 */
function collectAllConditions(scenario: Scenario): Array<[string, string]> {
  const conditions: Array<[string, string]> = [];

  // Check achievements for conditions
  if (scenario.metadata?.achievements) {
    for (let i = 0; i < scenario.metadata.achievements.length; i++) {
      const achievement = scenario.metadata.achievements[i];
      if (achievement?.condition) {
        conditions.push([
          achievement.condition,
          `metadata.achievements[${i}].condition`,
        ]);
      }
    }
  }

  const inventory = scenario.metadata?.inventory;
  if (inventory?.entries) {
    for (let i = 0; i < inventory.entries.length; i++) {
      const entry = inventory.entries[i];
      if (entry?.condition) {
        conditions.push([entry.condition, `metadata.inventory.entries[${i}].condition`]);
      }
      if (entry?.form === 'directory' && entry.groups) {
        for (let g = 0; g < entry.groups.length; g++) {
          const group = entry.groups[g];
          for (let p = 0; p < (group?.entries?.length ?? 0); p++) {
            const person = group?.entries?.[p];
            if (person?.condition) {
              conditions.push([
                person.condition,
                `metadata.inventory.entries[${i}].groups[${g}].entries[${p}].condition`,
              ]);
            }
          }
        }
      }
    }
  }

  forEachSceneBlock(scenario.scenes, (scene, scenePath) => {
    if (scene.condition) {
      conditions.push([scene.condition, `${scenePath}.condition`]);
    }

    // Check scene elements for conditions
    if (scene.elements) {
      for (let j = 0; j < scene.elements.length; j++) {
        const element = scene.elements[j];
        if (element?.clickAction?.condition) {
          conditions.push([
            element.clickAction.condition,
            `${scenePath}.elements[${j}].clickAction.condition`,
          ]);
        }
      }
    }

    if (scene.type === 'choice' && scene.options) {
      for (let l = 0; l < scene.options.length; l++) {
        const option = scene.options[l];
        if (option?.condition) {
          conditions.push([option.condition, `${scenePath}.options[${l}].condition`]);
        }
      }
    }

    if (scene.type === 'textOverlay' && scene.options) {
      for (let l = 0; l < scene.options.length; l++) {
        const option = scene.options[l];
        if (option?.condition) {
          conditions.push([option.condition, `${scenePath}.options[${l}].condition`]);
        }
      }
    }

    if (scene.type === 'chapterEnd') {
      for (let m = 0; m < scene.perfRows.length; m++) {
        const row = scene.perfRows[m];
        if (row?.condition) {
          conditions.push([row.condition, `${scenePath}.perfRows[${m}].condition`]);
        }
      }
    }

    if (scene.type === 'clickableScreen') {
      for (let z = 0; z < (scene.zones?.length ?? 0); z++) {
        const zone = scene.zones[z];
        if (zone?.condition) {
          conditions.push([zone.condition, `${scenePath}.zones[${z}].condition`]);
        }
      }
    }
  });

  return conditions;
}

/**
 * Validates a single click action (and recursively validates nextAction if present)
 */
function validateSingleClickAction(
  action: any,
  actionPath: string,
  errors: ValidationError[],
  depth = 0
): void {
  // Prevent infinite recursion
  if (depth > 10) {
    errors.push({
      message: `ClickAction chain is too deep (max 10 levels). Check for circular references.`,
      field: 'clickAction.nextAction',
      path: actionPath,
    });
    return;
  }

  // Valider changeBackground
  if (action.type === 'changeBackground') {
    if (typeof action.payload !== 'string') {
      errors.push({
        message: `ClickAction 'changeBackground' requires payload to be a string (background URL)`,
        field: 'clickAction.payload',
        path: `${actionPath}.payload`,
      });
    }
  }
  
  // Valider showTextOverlay
  if (action.type === 'showTextOverlay') {
    if (typeof action.payload !== 'object' || action.payload === null) {
      errors.push({
        message: `ClickAction 'showTextOverlay' requires payload to be a TextOverlayItem object`,
        field: 'clickAction.payload',
        path: `${actionPath}.payload`,
      });
    } else {
      const overlay = action.payload as any;
      if (!overlay.content) {
        errors.push({
          message: `TextOverlayItem in showTextOverlay must have a 'content' field`,
          field: 'clickAction.payload',
          path: `${actionPath}.payload`,
        });
      }
    }
  }

  // Validate nextAction recursively
  if (action.nextAction) {
    if (typeof action.nextAction !== 'object' || action.nextAction === null) {
      errors.push({
        message: `nextAction must be a valid ClickAction object`,
        field: 'clickAction.nextAction',
        path: `${actionPath}.nextAction`,
      });
    } else if (!action.nextAction.type) {
      errors.push({
        message: `nextAction must have a 'type' field`,
        field: 'clickAction.nextAction.type',
        path: `${actionPath}.nextAction.type`,
      });
    } else {
      // Recursively validate the nested action
      validateSingleClickAction(action.nextAction, `${actionPath}.nextAction`, errors, depth + 1);
    }
  }
}

/**
 * Validates click actions within a scenario
 * 
 * @param scenario - The scenario to validate
 * @returns ValidationResult with any errors found
 */
export function validateClickActions(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];
  
  forEachSceneBlock(scenario.scenes, (scene, scenePath) => {
    if (scene.type === 'gameOver' && !scene.goto) {
      errors.push({
        message: 'Game over block must define goto (restart checkpoint).',
        field: 'goto',
        path: `${scenePath}.goto`,
      });
    }
    if (scene.elements) {
      for (let j = 0; j < scene.elements.length; j++) {
        const element = scene.elements[j];
        if (element?.clickAction) {
          const actionPath = `${scenePath}.elements[${j}].clickAction`;
          validateSingleClickAction(element.clickAction, actionPath, errors);
        }
      }
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates clickableScreen scene blocks.
 */
export function validateClickableScreenScenes(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];

  forEachSceneBlock(scenario.scenes, (scene, scenePath) => {
    if (scene.type !== 'clickableScreen') return;

    if (scene.goto) {
      errors.push({
        message: 'clickableScreen scenes must not define goto at scene level — each zone carries its own goto.',
        field: 'goto',
        path: `${scenePath}.goto`,
      });
    }

    if (!scene.zones?.length) {
      errors.push({
        message: 'clickableScreen must define at least one zone.',
        field: 'zones',
        path: `${scenePath}.zones`,
      });
      return;
    }

    const zoneIdsInScene = new Set<string>();
    let allZonesConditional = true;

    for (let i = 0; i < scene.zones.length; i++) {
      const zone = scene.zones[i];
      const zonePath = `${scenePath}.zones[${i}]`;
      if (!zone) continue;

      if (!zone.id?.trim()) {
        errors.push({
          message: 'clickableScreen zone id is required.',
          field: 'id',
          path: `${zonePath}.id`,
        });
      } else if (zoneIdsInScene.has(zone.id)) {
        errors.push({
          message: `Duplicate zone id "${zone.id}" in clickableScreen scene.`,
          field: 'id',
          path: `${zonePath}.id`,
        });
      } else {
        zoneIdsInScene.add(zone.id);
      }

      if (!zone.label?.trim()) {
        errors.push({
          message: 'clickableScreen zone label is required.',
          field: 'label',
          path: `${zonePath}.label`,
        });
      }

      const pos = zone.position;
      if (
        !pos
        || typeof pos.x !== 'number'
        || typeof pos.y !== 'number'
        || typeof pos.width !== 'number'
        || typeof pos.height !== 'number'
      ) {
        errors.push({
          message: 'clickableScreen zone position must include x, y, width and height.',
          field: 'position',
          path: `${zonePath}.position`,
        });
      }

      if (!zone.goto?.trim()) {
        errors.push({
          message: 'clickableScreen zone goto is required.',
          field: 'goto',
          path: `${zonePath}.goto`,
        });
      }

      const shields = zone.pedaShields ?? 0;
      if (shields > 0 && !zone.pedaCategory) {
        errors.push({
          message: 'pedaCategory is required when pedaShields is set on a clickableScreen zone.',
          field: 'pedaCategory',
          path: `${zonePath}.pedaCategory`,
        });
      }
      if (zone.pedaCategory && !isValidPedaCategory(zone.pedaCategory)) {
        errors.push({
          message: `Invalid pedaCategory "${zone.pedaCategory}" on clickableScreen zone.`,
          field: 'pedaCategory',
          path: `${zonePath}.pedaCategory`,
        });
      }

      if (!zone.condition?.trim()) {
        allZonesConditional = false;
      }
    }

    if (allZonesConditional && scene.zones.length > 0) {
      errors.push({
        message: 'All clickableScreen zones are conditional — the player may have no playable zone. Add at least one zone without condition or a fallback.',
        field: 'zones',
        path: `${scenePath}.zones`,
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates the complete scenario structure
 * Performs all validation checks:
 * 1. Scene ID uniqueness
 * 2. Scene reference validity
 * 3. Condition syntax validation
 * 4. Basic structure validation
 * 
 * @param scenario - The scenario to validate
 * @returns ValidationResult with all errors found
 */
export function validateScenarioStructure(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];

  // Basic structure validation
  if (!scenario) {
    errors.push({
      message: 'Scenario is null or undefined',
      field: 'scenario',
    });
    return { isValid: false, errors };
  }

  if (!scenario.metadata) {
    errors.push({
      message: 'Scenario metadata is missing',
      field: 'metadata',
    });
  } else {
    if (!scenario.metadata.title) {
      errors.push({
        message: 'Scenario title is required',
        field: 'metadata.title',
        path: 'metadata.title',
      });
    }
    if (!scenario.metadata.startScene) {
      errors.push({
        message: 'Start scene is required',
        field: 'metadata.startScene',
        path: 'metadata.startScene',
      });
    }
    if (!scenario.metadata.achievements || !Array.isArray(scenario.metadata.achievements)) {
      errors.push({
        message: 'Scenario achievements array is required (can be empty)',
        field: 'metadata.achievements',
        path: 'metadata.achievements',
      });
    }

    // Validate skills structure if present
    if (scenario.metadata.skills) {
      const skillStructureResult = validateSkillStructure(scenario);
      errors.push(...skillStructureResult.errors);

      const skillUniquenessResult = validateSkillIdUniqueness(scenario);
      errors.push(...skillUniquenessResult.errors);
    }

    // Validate text overlay action references (skills and achievements)
    const textOverlayActionResults = validateTextOverlayActionReferences(scenario);
    errors.push(...textOverlayActionResults.errors);
  }

  if (!scenario.scenes || !Array.isArray(scenario.scenes)) {
    errors.push({
      message: 'Scenarios must have a scenes array',
      field: 'scenes',
    });
    return { isValid: false, errors };
  }

  if (scenario.scenes.length === 0) {
    errors.push({
      message: 'Scenario must have at least one scene',
      field: 'scenes',
      path: 'scenes',
    });
    return { isValid: false, errors };
  }

  if (!scenario.items || !Array.isArray(scenario.items)) {
    errors.push({
      message: 'Scenarios must have an items array (can be empty)',
      field: 'items',
    });
  }

  // Validate scene ID uniqueness
  const uniquenessResult = validateSceneIdUniqueness(scenario);
  errors.push(...uniquenessResult.errors);

  // Validate scene references
  const referencesResult = validateSceneReferences(scenario);
  errors.push(...referencesResult.errors);

  // Validate click actions
  const clickActionsResult = validateClickActions(scenario);
  errors.push(...clickActionsResult.errors);

  const clickableScreenResult = validateClickableScreenScenes(scenario);
  errors.push(...clickableScreenResult.errors);

  const inventoryResult = validateInventory(scenario);
  errors.push(...inventoryResult.errors);

  // Validate condition syntax
  const allConditions = collectAllConditions(scenario);
  for (const [condition, path] of allConditions) {
    if (!validateConditionSyntax(condition)) {
      errors.push({
        message: `Invalid condition syntax: "${condition}". Must match pattern "flag:name", "!flag:name", "var:name<op>value", "visited:sceneId" or "!visited:sceneId" where <op> is >, <, >=, <=, ==, != — or a comma-separated AND list of these`,
        field: 'condition',
        path,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

const INVENTORY_STATUSES = new Set<InventoryPersonStatus>([
  'suspect',
  'compromis',
  'confiance',
  'neutre',
]);

/**
 * Validates metadata.inventory catalogue when present.
 */
export function validateInventory(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = [];
  const inventory = scenario.metadata?.inventory;
  if (!inventory) {
    return { isValid: true, errors };
  }

  if (!Array.isArray(inventory.sections)) {
    errors.push({
      message: 'metadata.inventory.sections must be an array',
      field: 'inventory.sections',
      path: 'metadata.inventory.sections',
    });
    return { isValid: false, errors };
  }

  if (!Array.isArray(inventory.entries)) {
    errors.push({
      message: 'metadata.inventory.entries must be an array',
      field: 'inventory.entries',
      path: 'metadata.inventory.entries',
    });
    return { isValid: false, errors };
  }

  const sectionIds = new Set<string>();
  for (let i = 0; i < inventory.sections.length; i++) {
    const section = inventory.sections[i];
    const path = `metadata.inventory.sections[${i}]`;
    if (!section?.id || typeof section.id !== 'string') {
      errors.push({
        message: `Inventory section at index ${i} is missing required field "id"`,
        field: 'section.id',
        path: `${path}.id`,
      });
      continue;
    }
    if (sectionIds.has(section.id)) {
      errors.push({
        message: `Duplicate inventory section id "${section.id}"`,
        field: 'section.id',
        path: `${path}.id`,
      });
    }
    sectionIds.add(section.id);
    if (!section.title || typeof section.title !== 'string') {
      errors.push({
        message: `Inventory section "${section.id}" is missing required field "title"`,
        field: 'section.title',
        path: `${path}.title`,
      });
    }
  }

  const allSceneIds = new Set(collectAllSceneIds(scenario.scenes));
  const entryIds = new Set<string>();

  for (let i = 0; i < inventory.entries.length; i++) {
    const entry = inventory.entries[i];
    const path = `metadata.inventory.entries[${i}]`;
    if (!entry?.id || typeof entry.id !== 'string') {
      errors.push({
        message: `Inventory entry at index ${i} is missing required field "id"`,
        field: 'entry.id',
        path: `${path}.id`,
      });
      continue;
    }
    if (entryIds.has(entry.id)) {
      errors.push({
        message: `Duplicate inventory entry id "${entry.id}"`,
        field: 'entry.id',
        path: `${path}.id`,
      });
    }
    entryIds.add(entry.id);

    if (!entry.section || !sectionIds.has(entry.section)) {
      errors.push({
        message: `Inventory entry "${entry.id}" has unknown section "${entry.section}"`,
        field: 'entry.section',
        path: `${path}.section`,
      });
    }

    if (!entry.name || typeof entry.name !== 'string') {
      errors.push({
        message: `Inventory entry "${entry.id}" is missing required field "name"`,
        field: 'entry.name',
        path: `${path}.name`,
      });
    }

    if (entry.condition && !validateConditionSyntax(entry.condition)) {
      errors.push({
        message: `Invalid condition on inventory entry "${entry.id}"`,
        field: 'entry.condition',
        path: `${path}.condition`,
      });
    }

    if (entry.form === 'directory') {
      if (!entry.groups || !Array.isArray(entry.groups) || entry.groups.length === 0) {
        errors.push({
          message: `Directory entry "${entry.id}" must have at least one group`,
          field: 'entry.groups',
          path: `${path}.groups`,
        });
      } else {
        for (let g = 0; g < entry.groups.length; g++) {
          const group = entry.groups[g];
          const groupPath = `${path}.groups[${g}]`;
          if (!group?.entries?.length) {
            errors.push({
              message: `Directory group at ${groupPath} must have entries`,
              field: 'group.entries',
              path: `${groupPath}.entries`,
            });
            continue;
          }
          for (let p = 0; p < group.entries.length; p++) {
            const person = group.entries[p];
            const personPath = `${groupPath}.entries[${p}]`;
            if (!person?.id || typeof person.id !== 'string') {
              errors.push({
                message: `Directory person at ${personPath} is missing "id"`,
                field: 'person.id',
                path: `${personPath}.id`,
              });
              continue;
            }
            if (entryIds.has(person.id)) {
              errors.push({
                message: `Duplicate inventory id "${person.id}" (directory person)`,
                field: 'person.id',
                path: `${personPath}.id`,
              });
            }
            entryIds.add(person.id);
            if (!person.name || typeof person.name !== 'string') {
              errors.push({
                message: `Directory person "${person.id}" is missing "name"`,
                field: 'person.name',
                path: `${personPath}.name`,
              });
            }
            if (!person.status || !INVENTORY_STATUSES.has(person.status)) {
              errors.push({
                message: `Directory person "${person.id}" has invalid status "${person.status}"`,
                field: 'person.status',
                path: `${personPath}.status`,
              });
            }
            if (person.condition && !validateConditionSyntax(person.condition)) {
              errors.push({
                message: `Invalid condition on directory person "${person.id}"`,
                field: 'person.condition',
                path: `${personPath}.condition`,
              });
            }
            if (person.condition) {
              for (const part of person.condition.split(',').map((s) => s.trim())) {
                const visitedMatch = part.match(/^!?visited:([a-zA-Z_][a-zA-Z0-9_]*)$/);
                if (visitedMatch?.[1] && !allSceneIds.has(visitedMatch[1])) {
                  errors.push({
                    message: `Directory person "${person.id}" references unknown scene "${visitedMatch[1]}"`,
                    field: 'person.condition',
                    path: `${personPath}.condition`,
                  });
                }
              }
            }
          }
        }
      }
    } else if (entry.condition) {
      for (const part of entry.condition.split(',').map((s) => s.trim())) {
        const visitedMatch = part.match(/^!?visited:([a-zA-Z_][a-zA-Z0-9_]*)$/);
        if (visitedMatch?.[1] && !allSceneIds.has(visitedMatch[1])) {
          errors.push({
            message: `Inventory entry "${entry.id}" references unknown scene "${visitedMatch[1]}"`,
            field: 'entry.condition',
            path: `${path}.condition`,
          });
        }
      }
    }

    if (entry.card) {
      let cardScene: Scene | undefined;
      forEachSceneBlock(scenario.scenes, (scene) => {
        if (!cardScene && scene.id === entry.card) {
          cardScene = scene;
        }
      });
      if (!cardScene || cardScene.type !== 'recordCard') {
        errors.push({
          message: `Inventory entry "${entry.id}" card "${entry.card}" must point to an existing recordCard scene`,
          field: 'entry.card',
          path: `${path}.card`,
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Helper function to format validation errors for display
 * 
 * @param errors - Array of validation errors
 * @returns Formatted string for display to users
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) {
    return 'No errors';
  }

  return errors
    .map((error, index) => {
      let message = `${index + 1}. ${error.message}`;
      if (error.path) {
        message += `\n   Location: ${error.path}`;
      }
      if (error.lineNumber) {
        message += `\n   Line: ${error.lineNumber}`;
      }
      return message;
    })
    .join('\n\n');
}

